import { IsStringEmpty, isStringNotEmpty, IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { createBitmask } from '../../Common/Utils/BitwiseOperations'
import { encrypt } from '../../Common/Utils/Security'
import { ANY_NUMBER_OF_TELSYS_AVAILABLE_VALUE, CapabilityBitFlags } from '../Constants'
import {
    CMEBranding,
    CMEBrandingBackend,
    CMEBrandingBackendEntry,
    CMEBrandingEntry,
    CMEBrandingRepeatableEntry,
    CMEBrandingSubItemBackendEntry,
    CMEBrandingSubItemEntry,
    CMEDetailsData,
    CMEFormHookData,
    ServicePackItemForm
} from '../Types'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { fileToDataUri } from '../../Common/Utils/FileUtils'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'

/**
 * Maps branding data to a backend-compatible format and collects files for upload.
 *
 * @param {CMEBranding} [branding] - The branding data to be processed.
 * @param isPreviewBrandingApplied - Whether the preview branding is applied.
 * @returns {Promise<[Definable<CMEBrandingBackend>, [string, File][]]>} - A promise that resolves to a tuple containing the processed branding data and an array of files.
 */
export const brandingMapper = async (
    branding?: CMEBranding,
    isPreviewBrandingApplied?: boolean
): Promise<[Definable<CMEBrandingBackend>, [string, File][]]> => {
    if (!branding) return [undefined, []]

    const files: [string, File][] = []

    const processEntry = async ([key, value]: CMEBrandingEntry, path?: string): Promise<CMEBrandingBackendEntry> => {
        const fullKey: string = path ? `${path}.${key}` : key

        if (typeof value === 'boolean') {
            return [key, value]
        }

        if (Array.isArray(value)) {
            const processedArray: CMEBrandingRepeatableEntry[] = await Promise.all(
                value.map((entry, index) => processRepeatableEntry(entry, `${fullKey}[${index}]`))
            )

            return [key, processedArray]
        }

        const processedObject = await processObjectEntries(value, fullKey)

        return [key, processedObject]
    }

    const processRepeatableEntry = async (
        entry: CMEBrandingRepeatableEntry,
        path: string
    ): Promise<CMEBrandingRepeatableEntry> =>
        Object.fromEntries(
            await Promise.all(Object.entries(entry).map(([key, value]) => processSubEntry([key, value], path)))
        )

    const processSubEntry = async (
        [key, value]: CMEBrandingSubItemEntry,
        path: string
    ): Promise<CMEBrandingSubItemBackendEntry> => {
        const fullKey: string = `${path}.${key}`

        if (value instanceof File) {
            if (value.type.includes('video')) {
                files.push([fullKey, value])

                return [key, 'video:upload:required']
            }

            return [key, await fileToDataUri(value)]
        }

        return [key, value]
    }

    const processObjectEntries = async (obj: Record<string, string | File | Record<string, string>>, path: string) =>
        Object.fromEntries(
            await Promise.all(Object.entries(obj).map(([key, value]) => processSubEntry([key, value], path)))
        )

    return [
        {
            ...(isPreviewBrandingApplied ? { isPreviewBrandingApplied: true } : {}),
            ...Object.fromEntries(
                await Promise.all(
                    Object.entries(branding).map(
                        (
                            entry: [
                                string,
                                (
                                    | boolean
                                    | Record<string, string | File | Record<string, string>>
                                    | CMEBrandingRepeatableEntry[]
                                )
                            ]
                        ): Promise<CMEBrandingBackendEntry> => processEntry(entry)
                    )
                )
            )
        },
        files
    ]
}

/**
 * CME send form data mapper.
 *
 * @param details The User data from JSON response.
 * @param domainProfile The domain data from JSON response.
 * @param cmeID The currently selected cmeID.
 * @param provisioning The provisioning data from JSON response.
 * @param licenseManagement The license management data from JSON response.
 * @param capabilities The capabilities data from JSON response.
 * @param branding The branding data from JSON response.
 * @param isPreviewBrandingApplied Whether the preview branding is applied.
 * @param owningPermissions The owning permissions the user has for the CME. If parameter is not supplied, it is assumed the user has full permission.
 * @returns {Promise<[CMEDetailsData, [string, File][]]>} - A promise that resolves to a tuple containing the processed CME data and an array of files.
 */
const CMESendDataMapper = (
    { details, domainProfile, provisioning, licenseManagement, capabilities, branding }: CMEFormHookData,
    cmeID?: string,
    isPreviewBrandingApplied?: boolean,
    nonce?: Nullable<string>,
    owningPermissions?: AdminBackendPermissionNames[]
): Promise<[Partial<CMEDetailsData>, [string, File][]]> =>
    brandingMapper(branding, isPreviewBrandingApplied).then(
        ([processedBranding, files]: [Definable<CMEBrandingBackend>, [string, File][]]): [
            Partial<CMEDetailsData>,
            [string, File][]
        ] => {
            let cmeDetailsData: Partial<CMEDetailsData> = {}

            const isNewCmeWithNoCxiCapability: boolean =
                !cmeID &&
                !IsVarEmpty(capabilities) &&
                !(capabilities.cxiTeams || capabilities.cxiBroadworks || capabilities.cxiWebex)

            const isCXIEnabled: boolean = capabilities.cxiBroadworks || capabilities.cxiTeams || capabilities.cxiWebex

            const userHasFullPermission: boolean = IsVarEmpty(owningPermissions)

            if (
                userHasFullPermission ||
                owningPermissions?.includes(AdminBackendPermissionNames.EDIT_OWN_CME_DOMAIN_PROFILE)
            ) {
                cmeDetailsData = {
                    FromAddressName: domainProfile.fromaddressname,
                    CompanyName: domainProfile.companyname,
                    UseAuthentication: domainProfile.useauthentication,
                    IdentityProviderIDs: details.providerids,
                    Description: details.description,
                    DisableAkixiLogin: details.disableAkixiLogin,
                    FromAddressEmail: domainProfile.fromaddressemail,
                    ServerIPAddress: domainProfile.serveripaddress,
                    CommunicationActivated: domainProfile.communicationactive,
                    ServerTCPPort: `${domainProfile.tcpport}`,
                    CMEnvironmentID: !IsStringEmpty(details.id) ? details.id : cmeID ?? '',
                    CXIAccessCode: domainProfile.cxiAccessCode,
                    Domain: domainProfile.domain,
                    AppDownloadURL: domainProfile.teamsappdownloadurl,
                    CXIClientDownloadURLWin: isCXIEnabled ? domainProfile.cxiClientWindowsUrl : EMPTY_STRING,
                    CXIClientDownloadURLMac: isCXIEnabled ? domainProfile.cxiClientMacUrl : EMPTY_STRING,
                    SearchAndCallAppDownloadURL: isCXIEnabled ? domainProfile.searchAndCallUrl : EMPTY_STRING,
                    AuthUsername: domainProfile.username,
                    AuthPassword: encrypt(domainProfile.password, nonce ?? null)
                }
            }

            if (
                userHasFullPermission ||
                owningPermissions?.includes(AdminBackendPermissionNames.EDIT_OWN_CME_PROVISIONING)
            ) {
                cmeDetailsData = {
                    ...cmeDetailsData,
                    ProvisioningConfig: {
                        BroadworksProvisioning: {
                            ServicePacks: provisioning?.broadworks?.servicepacks
                                ? provisioning?.broadworks?.servicepacks?.map(
                                      (servicePackItem: ServicePackItemForm) => ({
                                          Value: servicePackItem.Value,
                                          ItemType: servicePackItem.ItemType,
                                          Enabled: servicePackItem.Enabled,
                                          TelSysIDs: servicePackItem.TelSysIDs.join(','),
                                          SendWelcomeEmail: servicePackItem.SendWelcomeEmail
                                      })
                                  )
                                : []
                        }
                    }
                }
            }

            if (
                !IsVarEmpty(branding) &&
                processedBranding &&
                (userHasFullPermission ||
                    owningPermissions?.includes(AdminBackendPermissionNames.EDIT_OWN_CME_BRANDING))
            ) {
                cmeDetailsData = {
                    ...cmeDetailsData,
                    BrandingConfig: processedBranding
                }
            }

            if (userHasFullPermission) {
                cmeDetailsData = {
                    ...cmeDetailsData,
                    ParentCMEnvironmentID: details.parentcme,
                    BillingTag: details.billingid,
                    LicenseCapManagement: {
                        EssentialsCap:
                            licenseManagement.EssentialsCap === EMPTY_STRING
                                ? null
                                : Number(licenseManagement.EssentialsCap),
                        ProfessionalCap:
                            licenseManagement.ProfessionalCap === EMPTY_STRING
                                ? null
                                : Number(licenseManagement.ProfessionalCap),
                        EnterpriseCap:
                            licenseManagement.EnterpriseCap === EMPTY_STRING
                                ? null
                                : Number(licenseManagement.EnterpriseCap),
                        CallCentreAgentCap:
                            licenseManagement.CallCentreAgentCap === EMPTY_STRING
                                ? null
                                : Number(licenseManagement.CallCentreAgentCap),
                        CallCentreSupervisorCap:
                            licenseManagement.CallCentreSupervisorCap === EMPTY_STRING
                                ? null
                                : Number(licenseManagement.CallCentreSupervisorCap),
                        CXIntegrationCap:
                            licenseManagement.CXIntegrationCap === EMPTY_STRING || isNewCmeWithNoCxiCapability
                                ? null
                                : Number(licenseManagement.CXIntegrationCap)
                    },
                    Capabilities: createBitmask(
                        capabilities.cxaBroadworks ? CapabilityBitFlags.CXA_BROADWORKS : 0,
                        capabilities.cxaWebex ? CapabilityBitFlags.CXA_WEBEX : 0,
                        capabilities.cxaTeams ? CapabilityBitFlags.CXA_TEAMS : 0,
                        capabilities.legacyTelephony ? CapabilityBitFlags.LEGACY_TELEPHONY : 0,
                        capabilities.cxiBroadworks ? CapabilityBitFlags.CXI_BROADWORKS : 0,
                        capabilities.cxiTeams ? CapabilityBitFlags.CXI_TEAMS : 0,
                        capabilities.cxiWebex ? CapabilityBitFlags.CXI_WEBEX : 0,
                        capabilities.legacy ? CapabilityBitFlags.CXA_LEGACY : 0,
                        capabilities.enterprise ? CapabilityBitFlags.ENTERPRISE : 0
                    ),
                    MaxTelsysCount:
                        isStringNotEmpty(capabilities.maxTelsysCount) && !isNaN(Number(capabilities.maxTelsysCount))
                            ? Number(capabilities.maxTelsysCount)
                            : ANY_NUMBER_OF_TELSYS_AVAILABLE_VALUE,
                    ShowUiVersionToggle: capabilities.showUiVersionToggle
                }
            }

            return [cmeDetailsData, files]
        }
    )

export default CMESendDataMapper
